import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { authTokens } from '@/services/authTokens';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5100';

/**
 * Escuta mudanças realtime em signage_tvs via Server-Sent Events.
 * Substitui o Supabase Realtime (que foi removido na migração de auth).
 *
 * Comportamento: cada evento "tvs:changed" invalida o cache do react-query
 * pra forçar refetch. Reconecta automaticamente se a conexão cair.
 */
export function useRealtimeTvs(tenantId: number | null) {
  const qc = useQueryClient();

  useEffect(() => {
    if (!tenantId) return;

    let es: EventSource | null = null;
    let backoffMs = 1000;
    let cancelled = false;

    function connect() {
      if (cancelled) return;
      const token = authTokens.getAccessToken();
      if (!token) return; // sem auth ainda; o effect re-roda quando entrar

      const url = `${API_URL}/api/signage/realtime/tvs?access_token=${encodeURIComponent(token)}`;
      es = new EventSource(url);

      es.addEventListener('tvs:changed', () => {
        qc.invalidateQueries({ queryKey: ['tvs'] });
        backoffMs = 1000;
      });

      es.addEventListener('connected', () => {
        backoffMs = 1000;
      });

      es.onerror = () => {
        es?.close();
        es = null;
        if (cancelled) return;
        // Reconnect com backoff exponencial (cap 30s)
        setTimeout(connect, backoffMs);
        backoffMs = Math.min(backoffMs * 2, 30_000);
      };
    }

    connect();

    return () => {
      cancelled = true;
      es?.close();
      es = null;
    };
  }, [tenantId, qc]);
}
